import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm z-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-black cursor-pointer" onClick={() => navigate('/')}>Koru Talent</h1>
            </div>
            <Button 
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-burgundy-800 hover:text-burgundy-900"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-black mb-4">Privacy Policy</h1>
          <p className="text-gray-600 mb-8">Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

          <div className="space-y-8 text-gray-700 leading-relaxed">
            <section>
              <h2 className="text-2xl font-bold text-black mb-4">1. Introduction</h2>
              <p>
                Koru Talent ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, 
                use, disclose, and safeguard your information when you visit our website or use our recruitment services.
              </p>
              <p className="mt-4">
                We are registered in the United Kingdom and comply with the UK General Data Protection Regulation (UK GDPR) and the 
                Data Protection Act 2018.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4">2. Information We Collect</h2>
              <h3 className="text-xl font-semibold text-burgundy-800 mb-3">2.1 Personal Information</h3>
              <p>We may collect personal information that you voluntarily provide to us when you:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Register for our services</li>
                <li>Submit your CV or application</li>
                <li data-testid="privacy-email-enquiries">Email us with an enquiry</li>
                <li>Subscribe to our newsletter</li>
              </ul>
              <p className="mt-4">This information may include:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Name and contact details (email address, phone number, postal address)</li>
                <li>Employment history and qualifications</li>
                <li>CV and cover letter</li>
                <li>Professional references</li>
                <li>Right to work documentation</li>
                <li>Any other information you choose to provide</li>
              </ul>

              <h3 className="text-xl font-semibold text-burgundy-800 mb-3 mt-6">2.2 Automatically Collected Information</h3>
              <p>When you visit our website, we may automatically collect:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>IP address and browser type</li>
                <li>Device information</li>
                <li>Pages visited and time spent on pages</li>
                <li>Referring website addresses</li>
                <li>Cookie data</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4">3. How We Use Your Information</h2>
              <p>We use your personal information for the following purposes:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>To provide recruitment services and match you with suitable job opportunities</li>
                <li>To communicate with you about job opportunities and our services</li>
                <li>To process job applications and facilitate interviews</li>
                <li>To verify your qualifications and employment history</li>
                <li>To comply with legal obligations and regulatory requirements</li>
                <li>To improve our website and services</li>
                <li>To send you marketing communications (with your consent)</li>
                <li>To maintain records and manage our business operations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4">4. Legal Basis for Processing</h2>
              <p>Under UK GDPR, we process your personal data based on:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li><strong>Consent:</strong> When you have given explicit permission for specific purposes</li>
                <li><strong>Contract:</strong> To fulfil our recruitment services agreement with you</li>
                <li><strong>Legal Obligation:</strong> To comply with employment law and regulatory requirements</li>
                <li><strong>Legitimate Interests:</strong> To operate our business and provide recruitment services</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4">5. Data Sharing and Disclosure</h2>
              <p>We may share your information with:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li><strong>Employers:</strong> When presenting you for job opportunities</li>
                <li><strong>Service Providers:</strong> Third-party services that help us operate our business (e.g., IT support, email services)</li>
                <li><strong>Professional Advisers:</strong> Lawyers, accountants, and other professional consultants</li>
                <li><strong>Regulatory Bodies:</strong> When required by law or to comply with legal obligations</li>
              </ul>
              <p className="mt-4">
                We do not sell, rent, or trade your personal information to third parties for marketing purposes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4">6. Data Retention</h2>
              <p>
                We retain your personal data for as long as necessary to fulfil the purposes for which it was collected. 
                For candidates, this is typically:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li><strong>Active candidates:</strong> While you are actively seeking employment through us</li>
                <li><strong>Inactive candidates:</strong> Up to 2 years after your last interaction with us</li>
                <li><strong>Placed candidates:</strong> Up to 6 years for tax and employment law purposes</li>
              </ul>
              <p className="mt-4">
                You may request deletion of your data at any time, subject to our legal obligations.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4">7. Your Rights</h2>
              <p>Under UK GDPR, you have the following rights:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li><strong>Right of Access:</strong> Request a copy of your personal data</li>
                <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete data</li>
                <li><strong>Right to Erasure:</strong> Request deletion of your personal data</li>
                <li><strong>Right to Restriction:</strong> Limit how we use your data</li>
                <li><strong>Right to Data Portability:</strong> Receive your data in a portable format</li>
                <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent for specific processing activities</li>
              </ul>
              <p className="mt-4">
                To exercise any of these rights, please contact us at <a href="mailto:christian@korutalent.co.uk" className="text-burgundy-800 hover:text-burgundy-900 underline">christian@korutalent.co.uk</a>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4">8. Cookies</h2>
              <p>
                Our website uses cookies to enhance your browsing experience. Cookies are small text files stored on your device. 
                We use:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li><strong>Essential Cookies:</strong> Required for the website to function properly</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our website</li>
                <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
              </ul>
              <p className="mt-4">
                You can control cookies through your browser settings. Please note that disabling cookies may affect website functionality.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4">9. Data Security</h2>
              <p>
                We implement appropriate technical and organisational measures to protect your personal data against unauthorised access, 
                alteration, disclosure, or destruction. These measures include:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Secure data storage and encryption</li>
                <li>Access controls and authentication</li>
                <li>Regular security assessments</li>
                <li>Staff training on data protection</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4">10. International Data Transfers</h2>
              <p>
                Your personal data is primarily stored and processed within the United Kingdom. If we need to transfer your data 
                outside the UK, we will ensure appropriate safeguards are in place in accordance with UK GDPR requirements.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4">11. Children's Privacy</h2>
              <p>
                Our services are not directed to individuals under the age of 16. We do not knowingly collect personal information 
                from children. If you become aware that a child has provided us with personal data, please contact us.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4">12. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date. 
                We encourage you to review this Privacy Policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4" data-testid="privacy-email-heading">13. Email Us</h2>
              <p>If you have any questions or concerns about this Privacy Policy or our data practices, please email us:</p>
              <div className="mt-4 p-6 bg-gray-50 rounded-lg border border-gray-200">
                <p className="font-semibold text-black">Koru Talent</p>
                <p className="mt-2">Email: <a href="mailto:christian@korutalent.co.uk" className="text-burgundy-800 hover:text-burgundy-900 underline">christian@korutalent.co.uk</a></p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4">14. Complaints</h2>
              <p>
                If you believe your data protection rights have been violated, you have the right to lodge a complaint with the 
                Information Commissioner's Office (ICO):
              </p>
              <div className="mt-4 p-6 bg-gray-50 rounded-lg border border-gray-200">
                <p className="font-semibold text-black">Information Commissioner's Office</p>
                <p className="mt-2">Website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-burgundy-800 hover:text-burgundy-900 underline">www.ico.org.uk</a></p>
                <p>Helpline: 0303 123 1113</p>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center text-gray-400">
          <p>&copy; {new Date().getFullYear()} Koru Talent. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;